/**
 * Preview Journey — sample dataset builder.
 *
 * D5, D6, D20, D21: MyCraftz Preview reuses the same MyCraftz screen as
 * Subscriber. Preview data is derived directly from the founder-approved
 * showcase blueprint returned by /api/mycraftz/showcase/{slug}. We do NOT
 * fabricate stats, streaks, or fake analytics (D25 / B5).
 *
 * Every row is clearly marked as a Sample. Mutations are disabled in
 * Preview — see MyCraftzPage handleCheckin / handleDelete guards.
 */
import { MYCRAFTZ_SHOWCASE, SHOWCASE_TITLES } from "./mycraftzShowcase";

/** The 5 showcase books, keyed by MyCraftz slug. */
export const PREVIEW_SLUGS = SHOWCASE_TITLES.map((t) => MYCRAFTZ_SHOWCASE[t].slug);

/**
 * Build a single "installed CraftPrint" row from a backend showcase payload.
 * Shape must mirror the /api/mycraftz/installed response the Subscriber view
 * already consumes — otherwise the ActiveCraftPrintCard component would
 * need changes, which is forbidden by D21.
 *
 * Note: percent, current_streak, days_completed and week are all zero /
 * empty on purpose. We only mark the CraftPrint as "installed for
 * demonstration". No statistics are fabricated (D25 / B5).
 */
function buildPreviewRow(payload) {
  const cp = payload?.craftprint || {};
  return {
    id: `preview-${payload.slug}`,
    book_slug: payload.slug,
    book_title: payload.title,
    idea_name: cp.idea_name || "Sample Craft Idea",
    duration_days: cp.duration_days || 30,
    status: "active",
    percent: 0,
    current_streak: 0,
    days_completed: 0,
    week: [],
    checkins: [],
    reflections: [],
    craftprint: cp,
    is_preview_sample: true, // consumed by MyCraftzPage to render a "Sample" ribbon
  };
}

/**
 * Fetch all 5 showcase CraftPrint blueprints in parallel and return them as
 * a Subscriber-shaped `installed` array. Preview state is entirely
 * client-side; nothing is persisted server-side (D22).
 */
export async function loadPreviewInstalled(apiBaseUrl) {
  const axios = (await import("axios")).default;
  const requests = PREVIEW_SLUGS.map((slug) =>
    axios.get(`${apiBaseUrl}/mycraftz/showcase/${slug}`).then((r) => r.data)
  );
  const results = await Promise.all(requests);
  return results.map(buildPreviewRow);
}
